import { AssetGroup } from '@play-co/timestep-core';
import StateObserver, { now, user } from 'src/StateObserver';
import {
  openPopupPromise,
  PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import {
  cardsGetCollections,
  cardsGetOwnedCardCount,
  cardsGetReceivedCards,
  cardsGetRewardsStatus,
  cardsGetTradeableCards,
  cardsCanStartTrade,
  cardsHasAnyPendingTrades,
  cardsHasPendingTrades,
  cardsIsAvailable,
  cardsIsEnabled,
  cardsShouldAutoOpenFirstTime,
} from 'src/replicant/cards/cards.getters';
import {
  CardId,
  CardsInventory,
  CardsRewardId,
  CollectionId,
  cardCollections,
  cardsAlbumCompleteRewards,
  cardsUnlockLevel,
  collectionColors,
} from 'src/replicant/cards/cards.ruleset';
import type { CollectionsHeaderState } from './components/CardCollectionsHeader';
import type { CardCollectionState } from './components/CardCollectionIcon';
import type { CollectionListState } from './components/CardCollectionsList';
import type { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { Feature } from 'src/replicant/analytics';
import { track } from 'src/lib/analytics/utils';
import {
  cardsGetAnalyticsContext,
  type CardsDeliveryMethod,
  type CardsViewOrigin,
} from 'src/replicant/cards/cards.analytics';
import type { CardsInviteRequestUnavailableReason } from 'src/replicant/cards/cards.types';
import { captureGenericError } from 'src/lib/sentry';
import { withLoading } from 'src/lib/utils';
import {
  CardPackID,
  CardPackTier,
} from 'src/replicant/cardpack/cardpack.ruleset';
import i18n from 'src/lib/i18n/i18n';
import type { PopupCardPickerCard } from './popups/PopupCardPicker';
import type { CardState } from './components/Card';
import {
  cardsGetRequestCandidates,
  cardsGetTradeCandidates,
  cardsRemoveTradeCandidateFromCache,
} from './cards.friends';
import { openProfile } from '../profile/profile.sequence';

/**
 * Ensure the current player has consented to a public profile before using a
 * Cards social feature.
 *
 * @returns True when a public profile already exists or was created.
 */
export async function cardsEnsurePublicProfile(
  origin?: PopupDialogOrigin,
): Promise<boolean> {
  if (user().publicProfile) return true;

  await openProfile(origin);
  return !!user().publicProfile;
}

/**
 * Open the cards popup
 */
export async function cardsOpenPopup(
  origin: PopupDialogOrigin | undefined,
): Promise<boolean> {
  const state = user();
  if (!cardsIsAvailable(state)) return false;

  // Only show the intro popup if user never seen it
  const seenAt = state.cards?.seenAt;

  // Capture the same notification state currently displayed on the Cards HUD.
  const hasRedDot = cardsHasNewStuff();

  // Track the collection open before seenAt changes its first-view state.
  track('CardsView', {
    origin: cardsGetAnalyticsViewOrigin(origin),
    isFirstView: !seenAt,
    hasRedDot,
    ...cardsGetAnalyticsContext(state),
  });

  // Update the seenAt timestamp for the current event
  await StateObserver.invoke.cardsUpdateSeenAt();

  if (!seenAt) {
    const ok = await openPopupPromise('popupCardsInfo', {
      origin,
    });
    if (!ok) return false;
  }

  // Colect cards sent from other users, if any
  await cardsCollectReceived();

  if (cardsHasPendingTrades(user(), now())) {
    await cardsOpenTradesPopup();
  }

  // Open all unopened packs
  await cardsOpenAllPacks();

  // Open the main cards popup
  await openPopupPromise('popupCardCollections', {
    ...cardsGetCollectionsPopupState(),
    origin,
  });

  return true;
}

/**
 * Open the Cards first-time discovery flow if the feature is unlocked and the
 * user has not seen it yet.
 *
 * @param origin Source surface used for popup analytics.
 * @returns True when the Cards popup was opened.
 */
export async function cardsAutoOpenFirstTime(
  origin: PopupDialogOrigin | undefined,
) {
  if (!cardsShouldAutoOpenFirstTime(user())) return false;

  return await cardsOpenPopup(origin);
}

/**
 * Public Cards HUD sequence for users who can see the Cards button but have not
 * reached the unlock level yet.
 *
 * @example
 * if (cardsIsLocked(user())) {
 *   await cardsOpenLockedPopup('hud');
 *   return;
 * }
 */
export async function cardsOpenLockedPopup(origin: PopupDialogOrigin) {
  await openPopupPromise('popupModalWhite', {
    title: i18n('cards.lockedTitle'),
    message: i18n('cards.lockedMessage', {
      level: cardsUnlockLevel,
    }),
    origin,
    tracking: {
      feature: Feature.CARDS._,
      $subFeature: Feature.CARDS.SIDE_BUTTON,
    },
  });
}

/**
 * Open the pending card trades popup, optionally focused on a specific trade.
 */
export async function cardsOpenTradesPopup(tradeId?: string) {
  if (!cardsIsEnabled(user())) return false;

  // Ensure the user has a public profile before opening the trade inbox.
  const hasPublicProfile = await cardsEnsurePublicProfile();
  if (!hasPublicProfile) return false;

  await openPopupPromise('popupCardTrades', { tradeId });
  return true;
}

/**
 * Open the card inbox popup when there are actionable pending trades to show.
 */
export async function cardsOpenInboxPopup() {
  if (!cardsIsEnabled(user())) return false;
  if (!cardsHasAnyPendingTrades(user(), now())) return false;

  return cardsOpenTradesPopup();
}

/**
 * Open card details after ensuring the player can use Cards social actions.
 */
export async function cardsOpenCardDetails(cardState: CardState) {
  // Ensure the user has a public profile before opening card details.
  const hasPublicProfile = await cardsEnsurePublicProfile();
  if (!hasPublicProfile) return undefined;

  return openPopupPromise('popupCardDetails', cardState);
}

/**
 * Values required to send a card in response to a card-request landing.
 */
export type CardsSendRequestResponseArgs = {
  targetId: string;
  requestMethod: CardsDeliveryMethod;
} & (
  | { kind: 'send'; cardId: CardId }
  | { kind: 'redeem'; requestedCardId: CardId }
);

/**
 * Outcome of sending a card from the card-request response popup.
 */
export type CardsSendRequestResponseResult =
  | { outcome: 'sent' }
  | { outcome: 'redeemed'; cardState: CardState }
  | {
      outcome: 'rejected';
      reason: CardsInviteRequestUnavailableReason;
    };

/**
 * Send an owned duplicate or redeem an invite card in response to a request.
 *
 * @param args Card, requester, delivery, and optional redemption details.
 * @returns The send outcome and any card state needed by the response popup.
 */
export async function cardsSendRequestResponse(
  args: CardsSendRequestResponseArgs,
): Promise<CardsSendRequestResponseResult> {
  // Keep both request-response routes covered by the Cards send loading state.
  return withLoading<CardsSendRequestResponseResult>(
    async () => {
      // Existing players send one owned duplicate without redeeming the link.
      if (args.kind === 'send') {
        await StateObserver.invoke.cardsSendCard({
          cardId: args.cardId,
          userId: args.targetId,
          source: 'request',
          sendMethod: args.requestMethod,
        });

        // The duplicate was sent without changing the displayed card identity.
        return { outcome: 'sent' };
      }

      // Cardless invitees redeem the request before the resulting card is shown.
      const redemption = await StateObserver.invoke.cardsRedeemInviteRequest({
        inviterId: args.targetId,
        cardId: args.requestedCardId,
        requestMethod: args.requestMethod,
      });

      // Preserve the authoritative rejection reason for popup presentation.
      if (!redemption.granted) {
        return { outcome: 'rejected', reason: redemption.reason };
      }

      // Derive the granted card count from the updated Replicant state.
      const state = user();
      const currentTime = now();
      const cardState: CardState = {
        id: redemption.grantedCardId,
        count: cardsGetOwnedCardCount(
          state,
          redemption.grantedCardId,
          currentTime,
        ),
        new: false,
      };

      // Return the card state that the popup should display after redemption.
      return { outcome: 'redeemed', cardState };
    },
    { feature: Feature.CARDS._, subFeature: Feature.CARDS.SEND },
  );
}

export function cardsGetCollectionsPopupState(): CollectionsHeaderState &
  CollectionListState {
  return {
    ...cardsGetHeaderState(),
    collections: cardsGetCollectionsState(),
  };
}

/**
 * Let the player pick one of their tradeable cards for a card trade.
 *
 * @returns The selected card, or undefined when the player closes the picker or
 * has no cards available to trade.
 */
export async function cardsPickCardToTrade(opts?: {
  silentIfEmpty?: boolean;
}): Promise<PopupCardPickerCard | undefined> {
  const tradeableCards = Object.entries(
    cardsGetTradeableCards(user(), now()),
  ) as [CardId, number][];
  if (tradeableCards.length === 0) {
    if (opts?.silentIfEmpty) return undefined;

    await openPopupPromise('popupModalWhite', {
      title: i18n('cards.cardTrade.noTradeableCardsTitle'),
      message: i18n('cards.cardTrade.noTradeableCardsMessage'),
      button: i18n('basic.ok'),
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.SEND,
      },
    });
    return undefined;
  }

  return openPopupPromise('popupCardPicker', {
    title: i18n('cards.cardTrade.availableCardsTitle'),
    cards: tradeableCards.map(([id, count]) => ({
      id,
      count,
    })),
    buttonText: i18n('cards.cardTrade.propose'),
  });
}

/**
 * Let the player pick a trade candidate and send the proposed card trade.
 *
 * @returns The selected candidate id, or undefined when the player closes the
 * picker without selecting a candidate.
 */
export async function cardsProposeTradeToCandidate(
  cardId: CardId,
): Promise<string | undefined> {
  const candidates = await withLoading(
    () => cardsGetTradeCandidates(cardId).get(),
    {
      feature: Feature.CARDS._,
      subFeature: Feature.CARDS.SEND,
    },
  );

  const candidateId = await openPopupPromise('popupCardTradeProposeList', {
    candidates,
    cardId,
  });
  if (!candidateId) return undefined;

  const candidate = candidates.find(({ id }) => id === candidateId);
  if (!candidate) return undefined;

  // The daily allowance may have changed while the candidate picker was open.
  if (!cardsCanStartTrade(user(), now())) {
    await openPopupPromise('popupModalWhite', {
      title: i18n('cards.cardTrade.proposeTitle'),
      message: i18n('cards.detailsPopup.tradeLimitMessage'),
      button: i18n('basic.ok'),
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.TRADE,
      },
    });
    return undefined;
  }

  await StateObserver.invoke.cardsProposeTrade({
    cardId,
    userId: candidate.id,
    otherUserName: candidate.name,
    otherUserAvatar: candidate.photo,
  });

  // The successful proposal makes this candidate unavailable for this card.
  cardsRemoveTradeCandidateFromCache(cardId, candidate.id);

  await openPopupPromise('popupCardTradeSent', {
    cardId,
    otherUserName: candidate.name,
    otherUserAvatar: candidate.photo,
  });

  return candidate.id;
}

/**
 * Let the player pick another player to ask for a missing card.
 */
export async function cardsOpenRequestProposeList(
  cardId: CardId,
): Promise<void> {
  const candidates = await withLoading(
    () => cardsGetRequestCandidates(cardId).get(),
    {
      feature: Feature.CARDS._,
      subFeature: Feature.CARDS.REQUEST,
    },
  );

  await openPopupPromise('popupCardRequestProposeList', {
    candidates,
    cardId,
  });
}

/**
 * Returns the state name for the cards/collections header, based on the collected cards and rewards status
 * @param args
 * @returns
 */
function cardsGetHeaderStateName(args: {
  collected: number;
  totalCards: number;
  rewardsStatus: string;
}): 'rewardsAvailable' | 'complete' | 'inProgress' {
  return args.rewardsStatus === 'available'
    ? 'rewardsAvailable'
    : args.collected === args.totalCards
      ? 'complete'
      : 'inProgress';
}

/**
 * Open a card collection and continue through adjacent collections when the
 * player uses the popup navigation arrows.
 *
 * @param collectionId Collection to open first.
 * @returns A promise that resolves after the player closes without navigating.
 */
export async function cardsOpenPopupCards(
  collectionId: CollectionId,
): Promise<void> {
  // Read fresh card state each time a collection popup is opened.
  const cardsState = user().cards;
  const collection = cardCollections[collectionId];
  const owned = cardsState?.cardsOwned ?? {};
  const newCards = cardsState?.cardsNewlyAdded ?? {};
  const collected = collection.cards.filter((card) => owned[card]).length;
  const totalCards = collection.cards.length;
  const rewards = collection.rewards as Match2Reward[];
  let rewardsStatus = cardsGetRewardsStatus(user(), collectionId);
  let stateName = cardsGetHeaderStateName({
    collected,
    totalCards,
    rewardsStatus,
  });

  // Build the current collection contents before clearing its new markers.
  const cards = collection.cards.map((card) => ({
    id: card,
    new: !!newCards[card],
    count: owned[card] ?? 0,
  }));

  // Clear the newly added cards from this collection.
  await StateObserver.invoke.cardsClearNewlyAdded({
    ids: collection.cards.filter((card) => newCards[card]),
  });

  // Claim completed collection rewards before showing the collection.
  if (rewardsStatus === 'available') {
    await cardsCollectRewards(collectionId);
    rewardsStatus = cardsGetRewardsStatus(user(), collectionId);
    stateName = cardsGetHeaderStateName({
      collected,
      totalCards,
      rewardsStatus,
    });
  }

  // Open the collection and wait for a close or navigation direction.
  const navigationDirection = await openPopupPromise('popupCards', {
    stateName,
    id: collectionId,
    collected,
    totalCards,
    rewards,
    cards,
  });

  // A regular close ends the collection popup sequence.
  if (!navigationDirection) return;

  // Move in the requested direction and wrap at either end of the album.
  const collectionIds = cardsGetCollections();
  const currentCollectionIndex = collectionIds.indexOf(collectionId);
  const directionOffset = navigationDirection === 'previous' ? -1 : 1;
  const nextCollectionIndex =
    (currentCollectionIndex + directionOffset + collectionIds.length) %
    collectionIds.length;
  const nextCollectionId = collectionIds[nextCollectionIndex];

  // Open the newly selected collection and keep the original caller pending.
  return cardsOpenPopupCards(nextCollectionId);
}

/**
 * Get the asset paths for the cards feature
 */
export function cardsGetAssetsPaths() {
  if (!cardsIsAvailable(user())) return [];
  return AssetGroup.constructURLs([
    'assets/ui/cards/common',
    'assets/ui/cards/hudButton',
    'assets/ui/cards/popupCardCollections',
    'assets/ui/cards/popupCardsInfo',
    'assets/ui/cards/popupCardsPackReward',
  ]);
}

/**
 * Retrieve cards overall progress and rewards
 * @returns
 */
function cardsGetHeaderState(): CollectionsHeaderState {
  const collections = cardsGetCollectionsState();
  const totalCards = collections.reduce(
    (acc, { totalCards }) => acc + totalCards,
    0,
  );
  const collected = collections.reduce(
    (acc, { collected }) => acc + collected,
    0,
  );
  const rewardsStatus = cardsGetRewardsStatus(user(), 'album');
  const stateName = cardsGetHeaderStateName({
    collected,
    totalCards,
    rewardsStatus,
  });
  return {
    stateName,
    collected,
    totalCards,
    rewards: cardsAlbumCompleteRewards,
  };
}

/**
 * Retrieve the list of cards collections and their progress
 * @returns
 */
function cardsGetCollectionsState(): CardCollectionState[] {
  const cardsState = user().cards;
  const owned = cardsState?.cardsOwned ?? {};
  const newlyAdded = cardsState?.cardsNewlyAdded ?? {};
  const collections = cardsGetCollections();
  return collections.map((id) => {
    const collection = cardCollections[id];
    const cards = collection.cards;
    const totalCards = cards.length;
    const collected = cards.filter((card: CardId) => owned[card]).length;
    const newCards = cards.some((card: CardId) => newlyAdded[card]);
    const rewardsStatus = cardsGetRewardsStatus(user(), id);
    const rewardsAvailable = rewardsStatus === 'available';
    return {
      id,
      collected,
      totalCards,
      color: collectionColors[id],
      newCards,
      rewardsAvailable,
      interactive: true,
    };
  });
}

/**
 * Collect rewards for a specific cards set or the entire album
 * @param rewardId
 * @returns
 */
export async function cardsCollectRewards(rewardId: CardsRewardId) {
  // Check if the rewards are available before claiming them
  const rewardsStatus = cardsGetRewardsStatus(user(), rewardId);

  // Report an error if somehow user tries to claim rewards when they are not available.
  if (rewardsStatus !== 'available') {
    captureGenericError(
      'CardsError',
      "Can't claim rewards: rewards are not available",
    );
    return false;
  }

  // Claim the rewards available and retrieve the claimed rewards to show in the popup
  const rewards = await StateObserver.invoke.cardsClaimRewards({
    rewardId,
  });

  // Skip if no rewards were claimed
  if (!rewards) return false;

  // Show the rewards popup with the claimed rewards
  await openPopupPromise('popupCardsAlbumReward', {
    rewardId,
    rewards,
  });

  // Return true if rewards were claimed successfully
  return true;
}

/**
 * Collect all cards received from other users, if any
 * @returns
 */
export async function cardsCollectReceived() {
  const receivedCards = cardsGetReceivedCards(user(), now());
  const userIds = Object.keys(receivedCards) as string[];
  const cardsMap: CardsInventory = {};
  userIds.forEach((userId) => {
    const cardsReceivedMap = receivedCards[userId];
    const cardsIds = Object.keys(cardsReceivedMap) as CardId[];
    cardsIds.forEach((cardId) => {
      const count = cardsReceivedMap[cardId] ?? 0;
      cardsMap[cardId] = (cardsMap[cardId] || 0) + count;
    });
  });

  const receivedCardsIds = Object.keys(cardsMap) as CardId[];
  const cardsState = user().cards;
  const cardsDiscarded = cardsState?.cardTrades?.cardsDiscarded ?? 0;

  if (cardsDiscarded > 0) {
    await openPopupPromise('popupCardResponse', {
      variant: 'receiveLimit',
    });
  }

  if (receivedCardsIds.length === 0) {
    // This also resets the discarded count
    await StateObserver.invoke.cardsCollectAllCardsReceived();

    return;
  }

  await cardsOpenReceivedPopup(cardsMap);

  // Collect all received cards after showing the received-cards animation.
  await StateObserver.invoke.cardsCollectAllCardsReceived();

  await cardsClaimCompletedCollections(receivedCardsIds);
}

/**
 * Reveal direct card rewards from a completed puzzle using the received-cards
 * popup animation.
 */
export async function cardsRevealReceivedRewards(rewards: Match2Reward[]) {
  const cardsMap: CardsInventory = {};
  rewards.forEach((reward) => {
    if (reward.type !== 'card') return;
    cardsMap[reward.cardId] = (cardsMap[reward.cardId] ?? 0) + 1;
  });

  const receivedCardIds = Object.keys(cardsMap) as CardId[];

  await cardsOpenReceivedPopup(cardsMap);

  await cardsClaimCompletedCollections(receivedCardIds);
}

/**
 * Open the received-cards popup for the provided card counts, batching large
 * sets into multiple popups.
 */
async function cardsOpenReceivedPopup(cardsMap: CardsInventory) {
  const receivedCardsIds = Object.keys(cardsMap) as CardId[];
  const newCards = receivedCardsIds.filter(cardsIsNewCard);

  // Show the cards received popup, in batches of 6 cards
  while (receivedCardsIds.length > 0) {
    const cards = receivedCardsIds.splice(0, 6);
    await openPopupPromise('popupCardsReceived', {
      cards: cards.map((id) => ({
        id,
        count: cardsMap[id] ?? 1,
        new: newCards.includes(id),
      })),
    });
  }
}

/**
 * Claim any completed collection rewards related to the provided card ids.
 */
async function cardsClaimCompletedCollections(cardIds: CardId[]) {
  if (cardIds.length === 0) return;

  const relatedCollections = cardsGetCollections().filter((collectionId) => {
    const collectionCards = cardCollections[collectionId].cards;
    return cardIds.some((cardId) => collectionCards.includes(cardId));
  });

  for (const collectionId of relatedCollections) {
    if (cardsGetRewardsStatus(user(), collectionId) !== 'available') continue;
    await cardsCollectRewards(collectionId);
  }
}

/**
 * Check if there is anything new in the cards feature for the user
 * @returns
 */
export function cardsHasNewStuff() {
  const cardsState = user().cards;
  const newlyAdded = Object.keys(cardsState?.cardsNewlyAdded ?? {}) as CardId[];
  const hasNewCards = newlyAdded.filter(cardsIsNewCard).length > 0;
  const hasReceivedCards =
    Object.keys(cardsState?.cardsReceived ?? {}).length > 0;
  const hasPendingTrades = cardsHasPendingTrades(user(), now());
  const hasUnopenedPacks = Object.keys(user().inventory.cardPacks).length > 0;
  const hasDiscardedCards = (cardsState?.cardTrades?.cardsDiscarded ?? 0) > 0;
  return (
    hasNewCards ||
    hasReceivedCards ||
    hasPendingTrades ||
    hasUnopenedPacks ||
    hasDiscardedCards
  );
}

/**
 * Check if a card is new
 * @param card
 * @returns
 */
function cardsIsNewCard(card: CardId) {
  const cardsState = user().cards;
  const owned = cardsState?.cardsOwned ?? {};
  const added = cardsState?.cardsNewlyAdded ?? {};

  // User doesn't have the card yet, so it's new
  if (!owned[card]) return true;

  // It is new if the card was added recently and the user has only one of it
  return !!added[card] && owned[card] === 1;
}

/**
 * Open a card pack available in the inventory, if any
 * @returns
 */
async function cardsOpenPack(id: CardPackID) {
  // Open the pack and get the cards inside
  const cards = await StateObserver.invoke.cardsOpenPack({ id });

  // Find out the newly added cards
  const newCards = cards.filter(cardsIsNewCard);

  // We need a safer way to get the tier
  const tier = Number(id.split('_')[1]) as CardPackTier;

  // Open the reward popup with the cards
  await openPopupPromise('popupCardsPackReward', {
    tier,
    cards: cards.map((id) => ({
      id,
      count: 1,
      new: newCards.includes(id),
    })),
  });

  await cardsClaimCompletedCollections(cards);
}

/** Normalize popup origins to the CardsView analytics vocabulary. */
function cardsGetAnalyticsViewOrigin(
  origin: PopupDialogOrigin | undefined,
): CardsViewOrigin {
  // Preserve the explicit HUD and post-level entry points.
  if (origin === 'hud') return 'hud';
  if (origin === 'postLevel') return 'post_level';

  // Undefined deep links and explicit launch flows both open during launch.
  return 'launch';
}

/**
 * Open a card pack by tier, if available
 * @param tier
 * @returns
 */
export async function cardsOpenPackByTier(tier: CardPackTier) {
  // Mount the pack id
  const packId = `card_${tier}` as CardPackID;

  // Check if the pack is available in the inventory
  const numPacks = user().inventory.cardPacks[packId]?.count ?? 0;

  // Packs should be available in advance, in order to be opened during the frenzy bar animation
  if (numPacks < 1) {
    captureGenericError(
      'CardsError',
      'No packs available for the specified tier',
    );
    return;
  }

  // Open the card pack
  await cardsOpenPack(packId);
}

/**
 * Open all card packs available in the inventory, if any
 */
async function cardsOpenAllPacks() {
  const cardPacks = user().inventory.cardPacks;
  const cardPacksKeys = Object.keys(cardPacks) as CardPackID[];
  const packsIds: CardPackID[] = [];

  // Add all packs to the list - some packs may have more than one
  for (const id of cardPacksKeys) {
    if (cardPacks[id].count > 0) {
      for (let i = 0; i < cardPacks[id].count; i++) {
        packsIds.push(id);
      }
    }
  }

  // Open all packs in sequence
  for (const id of packsIds) {
    await cardsOpenPack(id);
  }
}
